import { useEffect, useState } from 'react'
import { MultiStepStepLayout } from 'components/MultiStepFlow/MultiStepStepLayout/MultiStepStepLayout'
import { Button } from 'blaze-ui/Button'
import { FooterButtons, StickyFooter } from 'components/FirstTimeExperienceModal/components/Footer/Footer'
import { StickyFooterProvider } from 'providers/StickyFooterProvider/StickyFooterProvider'
import { useModals } from 'almanac-ui/Modal'
import { VisualStylesModal } from 'components/VisualStylesModal'
import Styles from './BrandVisualStylePreview.module.scss'
import { StyleGenerated } from '../BrandVisualStyleSelection/GenerationStage'
import { Heading } from 'blaze-ui/Heading/Heading'
import { Check02 } from 'blaze-ui/icons/16'
import { useStyledImage } from 'components/VisualStylePreviewCarousel/useStyledImage'
import { MediaFile } from 'components/VisualStylePreviewCarousel/VisualStylePreviewCarousel'
import { GenerationLoading } from '../BrandVisualStyleSelection/GenerationLoading'
import { useVisualStyles } from 'hooks/queries/visualStyles'

export interface BrandVisualStylePreviewProps {
  onNext: (selectedVisualStyle: StyleGenerated, stylesGenerated: StyleGenerated[]) => void
  onBack: () => void
  initialStylesGenerated?: StyleGenerated[]
  initialSelectedStyle?: StyleGenerated
  sourceImage: MediaFile
  brandKitId?: number
}

export const BrandVisualStylePreview = ({
  onNext,
  onBack,
  initialStylesGenerated = [],
  initialSelectedStyle,
  sourceImage,
  brandKitId,
}: BrandVisualStylePreviewProps) => {
  const { openModal } = useModals()
  const { data: visualStyles } = useVisualStyles()
  const [stylesGenerated, setStylesGenerated] = useState<StyleGenerated[]>(initialStylesGenerated)
  const [shouldGenerateNewStyle, setShouldGenerateNewStyle] = useState<boolean>(false)
  const [newStyleId, setNewStyleId] = useState<number | null>(null)
  const [selectedVisualStyleId, setSelectedVisualStyleId] = useState<number | null>(
    initialSelectedStyle?.id || stylesGenerated[0]?.id,
  )

  const newVisualStyle = (newStyleId && visualStyles?.find((style) => style.id === newStyleId)) || null
  const newStyle = useStyledImage(sourceImage, newStyleId, brandKitId, true)

  useEffect(() => {
    if (!stylesGenerated.length) {
      const style1 = visualStyles?.find((style) => style.name === 'Morning Contrast')
      const style2 = visualStyles?.find((style) => style.name === 'Sunset Studio')
      const style3 = visualStyles?.find((style) => style.name === 'Black and White Minimal')

      if (style1 && style2 && style3) {
        setSelectedVisualStyleId(style1.id)
        setStylesGenerated([
          {
            ...style1,
            styleUrl: style1.cover_image_url,
          },
          {
            ...style2,
            styleUrl: style2.cover_image_url,
          },
          {
            ...style3,
            styleUrl: style3.cover_image_url,
          },
        ])
      }
    }
  }, [selectedVisualStyleId, sourceImage, stylesGenerated.length, visualStyles])

  useEffect(() => {
    if (newStyle.styledUrl && !newStyle.isLoading && !stylesGenerated.find((style) => style.id === newStyleId)) {
      const replacedStyle = stylesGenerated[0]
      setStylesGenerated([
        {
          ...newStyle,
          id: newStyleId || 0,
          styleUrl: newStyle.styledUrl || '',
          name: newVisualStyle?.name || '',
          cover_image_url: newVisualStyle?.cover_image_url || '',
        },
        ...stylesGenerated.slice(1),
        replacedStyle,
      ])
      // setSelectedVisualStyleId(newStyleId)
    }
  }, [newStyle.styledUrl, newStyle.isLoading, newStyle, stylesGenerated, newStyleId, newVisualStyle])

  useEffect(() => {
    if (newStyleId && shouldGenerateNewStyle) {
      setShouldGenerateNewStyle(false)
      newStyle.triggerGeneration()
    }
  }, [newStyleId]) // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch brand kit to get brand_visual_kit
  const handleOpenVisualStylesModal = () => {
    openModal(VisualStylesModal, {
      selectedStyleId: selectedVisualStyleId,
      onConfirm: (styleId: number) => {
        if (!sourceImage) {
          const newStyleToAdd = visualStyles?.find((style) => style.id === styleId)
          if (!newStyleToAdd) return

          const newArray = [
            ...stylesGenerated.slice(1),
            {
              ...newStyleToAdd,
              styleUrl: newStyleToAdd.cover_image_url,
            },
          ]
          setStylesGenerated(newArray as StyleGenerated[])
          setSelectedVisualStyleId(styleId)
          return
        }

        const existingStyle = stylesGenerated.find((style) => style.id === styleId)
        const existingIndex = stylesGenerated.findIndex((style) => style.id === styleId)

        if (existingStyle) {
          if (existingIndex > 2) {
            const firstItem = stylesGenerated[0]
            const newArray = [
              existingStyle,
              ...stylesGenerated.slice(1, existingIndex),
              ...stylesGenerated.slice(existingIndex + 1),
              firstItem,
            ]
            setStylesGenerated(newArray)
          }
          setSelectedVisualStyleId(styleId)

          return
        }

        setSelectedVisualStyleId(styleId)
        setShouldGenerateNewStyle(true)
        setNewStyleId(styleId)
      },
    })
  }

  const handleNext = () => {
    if (!selectedVisualStyleId) return

    const selectedStyle = stylesGenerated.find((style) => style.id === selectedVisualStyleId) || {
      id: selectedVisualStyleId,
      styleUrl: '',
      name: '',
      cover_image_url: '',
    }

    onNext(selectedStyle, stylesGenerated)
  }

  return (
    <MultiStepStepLayout
      title="Select a photography style for your brand"
      description="We'll apply this style to all generated images to keep your content visually consistent"
      scrollContainerClassName={Styles.scrollContainer}
    >
      <StickyFooterProvider
        footer={
          <StickyFooter>
            <FooterButtons>
              <div className={Styles.footerSide}>
                <Button variant="tertiary" size="lg" onPress={onBack} aria-label="Back">
                  Back
                </Button>
              </div>
              <div className={Styles.footerCenter}>
                <Button
                  size="lg"
                  variant="secondary"
                  onPress={handleOpenVisualStylesModal}
                  isDisabled={newStyle.isLoading}
                  color="var(--dark-80)"
                >
                  Browse All Styles
                </Button>
              </div>
              <div className={Styles.footerSide}>
                <Button size="lg" onPress={handleNext} aria-label="Looks Good" isDisabled={newStyle.isLoading}>
                  Looks Good
                </Button>
              </div>
            </FooterButtons>
          </StickyFooter>
        }
      >
        <div className={Styles.stylesContainer}>
          {stylesGenerated.slice(0, 3).map((style, index) => (
            <div className={Styles.styleContainer} key={style.id} onClick={() => setSelectedVisualStyleId(style.id)}>
              {selectedVisualStyleId === style.id && (
                <div className={Styles.selectedCheck}>
                  <Check02 size={15} color="var(--dark-80)" />
                </div>
              )}
              {(!newStyle.isLoading || index !== 0) && (
                <img src={style.styleUrl} onContextMenu={(e) => e?.preventDefault()} alt={style.name} />
              )}

              {newStyle.isLoading && index === 0 && (
                <GenerationLoading
                  sourceImage={sourceImage}
                  stylesGeneration={newStyle}
                  onTransformedImageLoaded={() => {}}
                  isLoading={newStyle.isLoading}
                  withTag={false}
                />
              )}
              <div className={Styles.styleNameContainer}>
                <div className={Styles.styleName}>
                  <Heading color="var(--light-100)" level={3}>
                    {newStyle.isLoading && index === 0 ? newVisualStyle?.name : style.name}
                  </Heading>
                </div>
              </div>
              {index === 0 && !newStyleId && <div className={Styles.recommendedTag}>Recommended</div>}
            </div>
          ))}
        </div>
      </StickyFooterProvider>
    </MultiStepStepLayout>
  )
}
